/* eslint-disable react/prefer-stateless-function */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { PaginationPresentationProps } from "@insite/mobius/Pagination/Pagination";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Search from "@tIntermatic/Icons/Search";
import { getDocumentListFilters } from "@tIntermatic/Store/Components/DocumentListFilter/DocumentListFilterSelector";
import addDocumentsFilters from "@tIntermatic/Store/Components/DocumentListFilter/Handlers/AddDocumentListFilter";
import loadDocumentsFilters from "@tIntermatic/Store/Components/DocumentListFilter/Handlers/LoadDocumentListFilters";
import removeAllDocumentsFilters from "@tIntermatic/Store/Components/DocumentListFilter/Handlers/RemoveAllDocumentListFilters";
import removeDocumentsFilters from "@tIntermatic/Store/Components/DocumentListFilter/Handlers/RemoveDocumentListFilters";
import CustomApplicationState from "@tIntermatic/Store/CustomApplicationState";
import DocumentsListFiltersAccordionSection from "@tIntermatic/Widgets/FilterTypeAccordionSection";
import isMobile from "ismobilejs";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface IntDocumentsListModel {
    fields: {
        [fields.name]: string;
        [fields.source]: string;
        [fields.isEnglish]: boolean;
        [fields.isFrench]: boolean;
        [fields.isSpanish]: boolean;
        [fields.type]: string;
        [fields.language]?: string;
        [fields.isPdpLink]?: boolean;
        [fields.pdpLink]?: string;
    };
}

interface State extends CustomApplicationState {
    isFiltered: boolean;
    typesData: FacetModel[];
    languageData: FacetModel[];
    docsListData: IntDocumentsListModel[];
}

const enum fields {
    title = "title",
    documents = "documents",
    name = "name",
    source = "source",
    isEnglish = "isEnglish",
    isFrench = "isFrench",
    isSpanish = "isSpanish",
    type = "type",
    language = "language",
    isPdpLink = "isPdpLink",
    pdpLink = "pdpLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.documents]: IntDocumentsListModel[];
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: WidgetProps) => {
    const types: string[] = ownProps.fields.documents.map((doc: { fields: { type: string } }) => {
        return doc.fields.type;
    });
    const typesListFiltered = types.filter(function (elem, pos) {
        // eslint-disable-next-line eqeqeq
        return types.indexOf(elem) == pos;
    });
    const typesList: FacetModel[] = typesListFiltered.map<FacetModel>(o => ({
        id: o,
        name: o,
        count: 1,
        selected: false,
    }));

    const language: string[] = ["English", "French", "Spanish"];
    const languageList: FacetModel[] = language.map<FacetModel>(o => ({
        id: o,
        name: o,
        count: 1,
        selected: false,
    }));
    let documentsList = ownProps.fields.documents.map((document: IntDocumentsListModel) => {
        const languages: string[] = [];
        if (document.fields.isEnglish) {
            languages.push("English");
        }
        if (document.fields.isFrench) {
            languages.push("French");
        }
        if (document.fields.isSpanish) {
            languages.push("Spanish");
        }
        return {
            ...document,
            fields: {
                ...document.fields,
                language: languages.join(","),
            },
        };
    });

    const languageCode = state.context.session.language?.languageCode
        ? state.context.session.language.languageCode
        : "en";
    if (documentsList) {
        documentsList = documentsList.sort((a: IntDocumentsListModel, b: IntDocumentsListModel) =>
            a.fields.name.localeCompare(b.fields.name, languageCode, { sensitivity: "base" }),
        );
    }

    return {
        typesList,
        languageList,
        documentsList,
    };
};

const mapDispatchToProps = {
    getDocumentListFilters,
    addDocumentsFilters,
    loadDocumentsFilters,
    removeAllDocumentsFilters,
    removeDocumentsFilters,
};

export interface DocumentListStyles {
    dataTable: InjectableCss;
    pagination?: PaginationPresentationProps;
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    tableContainer?: InjectableCss;
    Rlptitle?: InjectableCss;
    listWrapper?: InjectableCss;
    subResourceNameLinkItem?: GridItemProps;
    columnHeader?: InjectableCss;
    columnData?: InjectableCss;
    cellCss?: InjectableCss;
    languageCellCss?: InjectableCss;
    typeLinks?: LinkPresentationProps;
    modelLinks?: LinkPresentationProps;
    ResourceContainer?: GridContainerProps;
    ResourceListContainer?: GridItemProps;
    ResourceListItemContainer?: GridItemProps;
    modelLinksText?: InjectableCss;
    searchTextField?: TextFieldPresentationProps;
}
export const documentListStyles: DocumentListStyles = {
    dataTable: {},
    tableContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
            overflow: auto;
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    container: {
        gap: 20,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 40px 12px 40px;
                    `,
                    css`
                        margin: 40px 30px 40px;
                    `,
                    css`
                        margin: 58px 0 20px 0;
                    `,
                    css`
                        margin: 58px 40px 20px 8px;
                    `,
                    css`
                        margin: 58px 40px 20px 8px;
                    `,
                ])}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    Rlptitle: {
        css: css`
            font-size: 21px;
            font-weight: bold;
            letter-spacing: 1.75px;
            line-height: 25px;
            color: #33333a;
            margin-left: 7px;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            font-size: 21px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 14px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 19px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                    ],
                    "min",
                )}
        `,
    },

    subResourceNameLinkItem: {
        width: 12,
        css: css`
            padding: 0 !important;
            text-transform: capitalize;
        `,
    },

    columnHeader: {
        css: css`
            border: 0px;
            background: #fff;
            height: 40px;
            > span {
                font-size: 12px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 25px;
                color: #33333a;
                text-transform: uppercase;
            }
        `,
    },

    columnData: {
        css: css`
            border: 0px;
            &:nth-child(odd) {
                background: #f4f6fa;
            }
            &:nth-child(even) {
                background-color: #fff;
            }
            > td[class^="DataTableCellBase"] {
                padding: 13px;
            }
        `,
    },

    cellCss: {
        css: css`
            height: 40px;
            border: 0px;
            text-transform: capitalize;
            > span {
                font-size: 16px;
                letter-spacing: 0;
                line-height: 24px;
                text-align: justify;
                color: #33333a;
            }
        `,
    },

    languageCellCss: {
        css: css`
            height: 40px;
            border: 0px;
            > span {
                font-size: 16px;
                letter-spacing: 0;
                line-height: 24px;
                text-align: justify;
                color: #33333a;
                text-transform: uppercase;
            }
        `,
    },
    modelLinks: {
        css: css`
            font-size: 16px;
            cursor: pointer;
            > span {
                font-size: 16px;
                letter-spacing: 0;
                line-height: 24px;
                text-align: justify;
                color: #33333a;
                text-transform: uppercase;
            }
        `,
    },
    ResourceListContainer: {
        width: [12, 12, 12, 3, 3],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 40px 10px 20px 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0 10px;
                    `,
                    css`
                        padding: 0;
                        margin-bottom: 70px !important;
                    `,
                    css`
                        padding: 0;
                        margin-bottom: 70px !important;
                    `,
                ])}
        `,
    },
    ResourceListItemContainer: {
        width: [12, 12, 12, 9, 9],
        css: css`
            border: 1px solid #e1e1e1;
            background-color: #ffffff;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 !important;
                        margin-bottom: 70px !important;
                    `,
                    css`
                        margin: 0 20px !important;
                        margin-bottom: 70px !important;
                    `,
                    css`
                        margin-top: 20px !important;
                        margin-bottom: 70px !important;
                    `,
                    css`
                        margin-top: -63px !important;
                        margin-bottom: 70px !important;
                        padding: 10px 20.5px 10px 24.5px;
                    `,
                    css`
                        margin-top: -63px !important;
                        margin-bottom: 70px !important;
                        padding: 10px 20.5px 10px 24.5px;
                    `,
                ])}
        `,
    },

    ResourceContainer: {
        gap: 0,
        css: css`
            width: 100%;
            max-width: 1140px;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 30px;
                    `,
                    null,
                    css`
                        padding: 0 30px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    typeLinks: {
        color: "#2e64b0",
        css: css`
            font-size: 16px;
            text-transform: capitalize;
        `,
    },
    modelLinksText: {
        css: css`
            font-size: 16px;
            letter-spacing: 0;
            line-height: 24px;
            text-align: justify;
            color: #33333a !important;
            text-transform: uppercase;
            cursor: default;
        `,
    },
    searchTextField: {
        iconProps: { src: Search },
        cssOverrides: {
            formField: css`
                > div {
                    padding-top: 20px;
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                    padding-bottom: 20px;
                    > span {
                        left: 14px !important;
                        top: 24px !important;
                        width: 29px !important;
                        height: 34px !important;
                        padding: 0px !important;
                        > svg {
                            stroke-width: 1;
                            color: #33333a !important;
                            height: 18px;
                            width: 18px;
                        }
                    }
                }
            `,
            inputSelect: css`
                border: 1px solid #cccccc;
                font-size: 16px;
                letter-spacing: 0;
                line-height: 42px;
                height: 42px;
                width: 100% !important;
                padding: 10px 10px 10px 46px;
                ::placeholder {
                    color: rgba(0, 0, 0, 0.5);
                    font-size: 16px;
                    letter-spacing: 0;
                    right: 5px;
                }
                @media all and (max-width: 992px) and (min-width: 0px) {
                    border: 1px solid #cccccc;
                    &:focus {
                        border: 1px solid #cccccc !important;
                        padding-left: 46px !important;
                        padding-right: 10px !important;
                    }
                }

                &:focus {
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    padding-left: 40px;
                    color: rgba(0, 0, 0, 0.5);
                    font-size: 16px;
                    letter-spacing: 0;
                    padding-left: 46px !important;
                    padding-right: 10px !important;
                }
            `,
        },
    },
};

const styles = documentListStyles;
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

class IntDocumentsList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            ...this.state,
            typesData: this.props.typesList,
            languageData: this.props.languageList,
            docsListData: this.props.documentsList,
        };
    }
    componentDidMount() {
        this.updateDocumentData();
    }
    updateTypeFilter = (filteredData: IntDocumentsListModel[]) => {
        const documentListFilter = getDocumentListFilters();
        const types: string[] = filteredData.map((doc: { fields: { type: string } }) => {
            return doc.fields.type;
        });
        const typesListFiltered = types.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return types.indexOf(elem) == pos;
        });
        const typesList: FacetModel[] = typesListFiltered.map<FacetModel>(o => ({
            id: o,
            name: o,
            count: 1,
            selected: documentListFilter.typeFilters.indexOf(o) !== -1,
        }));
        this.setState({
            typesData: typesList,
        });
    };

    updateLanguageFilter = (filteredData: IntDocumentsListModel[]) => {
        const documentListFilter = getDocumentListFilters();
        const language: string[] = ["English", "French", "Spanish"];
        const languageList: FacetModel[] = language.map<FacetModel>(o => ({
            id: o,
            name: o,
            count: 1,
            selected: documentListFilter.langFilters.indexOf(o) !== -1,
        }));
        this.setState({
            languageData: languageList,
        });
    };

    updateFilterData = (filteredData: IntDocumentsListModel[], _filterType: string, _isSelected: boolean) => {
        const documentListFilter = getDocumentListFilters();
        if (_filterType === "onLoad" || !_isSelected) {
            this.updateLanguageFilter(filteredData);
            this.updateTypeFilter(filteredData);
        } else if (_filterType === "language" && _isSelected) {
            this.updateTypeFilter(filteredData);
        } else if (_filterType === "type" && _isSelected) {
            this.updateLanguageFilter(filteredData);
        } else if (_filterType === "model" && _isSelected) {
            this.updateLanguageFilter(filteredData);
            this.updateTypeFilter(filteredData);
        }
        if (documentListFilter.typeFilters.length === 0 && documentListFilter.langFilters.length === 0) {
            this.updateLanguageFilter(filteredData);
            this.updateTypeFilter(filteredData);
            // window.location.hash = "";
            this.setState({ isFiltered: false });
        }
    };
    updateDocumentData = (_filterType = "onLoad", _isSelected = false, _isValue = "") => {
        const documents: IntDocumentsListModel[] = this.props.documentsList;
        const documentListFilter = getDocumentListFilters();
        let filteredData: IntDocumentsListModel[];
        if (!documents) {
            return null;
        }
        filteredData = documents;
        if (window.parent.location.href.indexOf("isFiltered=true") !== -1) {
            if (documentListFilter.langFilters.length !== 0) {
                const filteredLangData = filteredData.filter(elem => {
                    for (const langFilter of documentListFilter.langFilters) {
                        if (elem.fields.language && elem.fields.language.indexOf(langFilter) !== -1) {
                            return true;
                        }
                    }
                    return false;
                });
                filteredData = filteredLangData;
            }
            if (documentListFilter.typeFilters.length !== 0) {
                const filteredTypeData = filteredData.filter(
                    elem => documentListFilter.typeFilters.indexOf(elem.fields.type) !== -1,
                );
                filteredData = filteredTypeData;
            }
            if (_filterType.length !== 0 && _filterType == "model" && _isValue.length !== 0) {
                const filteredModelData = filteredData.filter(elem => {
                    if (elem.fields.name && elem.fields.name.toLowerCase().indexOf(_isValue.toLowerCase()) !== -1) {
                        return true;
                    }
                    return false;
                });
                filteredData = filteredModelData;
            }
            this.setState({
                isFiltered: true,
                docsListData: filteredData,
            });
        } else {
            this.props.removeAllDocumentsFilters();
            this.setState({
                isFiltered: false,
                docsListData: filteredData,
            });
        }
        this.updateFilterData(filteredData, _filterType, _isSelected);
    };
    render() {
        const onChangeDocType = (facet: FacetModel) => {
            // eslint-disable-next-line eqeqeq
            const objIndex = this.state.typesData.findIndex(obj => obj.id == facet.id);
            const tempTypeData = [...this.state.typesData];
            const type = { ...tempTypeData[objIndex] };
            type.selected = !facet.selected;
            tempTypeData[objIndex] = type;
            this.setState({ typesData: tempTypeData });
            if (type.selected) {
                this.props.addDocumentsFilters({ filterId: type.id, type: "doctype" });
            } else {
                this.props.removeDocumentsFilters({ filterId: type.id, type: "doctype" });
            }
            const searchParam = new URLSearchParams(window.location.hash.split("?")[1]);
            searchParam.set("isFiltered", "true");
            window.location.hash = `?${searchParam.toString()}`;
            this.updateDocumentData("type", type.selected, undefined);
        };
        const onChangeDocLang = (facet: FacetModel) => {
            // eslint-disable-next-line eqeqeq
            const objIndex = this.state.languageData.findIndex(obj => obj.id == facet.id);
            const tempLangData = [...this.state.languageData];
            const lang = { ...tempLangData[objIndex] };
            lang.selected = !facet.selected;
            tempLangData[objIndex] = lang;
            this.setState({ languageData: tempLangData });
            if (lang.selected) {
                this.props.addDocumentsFilters({ filterId: lang.id, type: "doclang" });
            } else {
                this.props.removeDocumentsFilters({ filterId: lang.id, type: "doclang" });
            }
            const searchParam = new URLSearchParams(window.location.hash.split("?")[1]);
            searchParam.set("isFiltered", "true");
            window.location.hash = `?${searchParam.toString()}`;
            this.updateDocumentData("language", lang.selected, undefined);
        };
        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newQuery = event.target.value;
            const searchParam = new URLSearchParams(window.location.hash.split("?")[1]);
            searchParam.set("isFiltered", "true");
            window.location.hash = `?${searchParam.toString()}`;
            this.updateDocumentData("model", true, newQuery);
        };
        return (
            <>
                <GridContainer {...styles.ResourceContainer}>
                    <GridItem {...styles.ResourceListContainer}>
                        <GridContainer {...styles.container}>
                            <StyledWrapper
                                css={css`
                                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                                    width: 100%;
                                `}
                            >
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        margin-bottom: 19px;
                                    `}
                                >
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                        `}
                                    >
                                        <LazyImage
                                            src="/UserFiles/Images/MiniLogo.jpg"
                                            css={css`
                                                width: 20px;
                                            `}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper>
                                        <Typography as="h3" {...styles.Rlptitle}>
                                            {this.props.fields.title}
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <TextField
                                {...styles.searchTextField}
                                // iconClickableProps={{ onClick: doSearch }}
                                // value={searchText}
                                onChange={handleInputChange}
                                // onKeyDown={handleKeyDown}
                                placeholder={translate("Search by Model #")}
                                type="text"
                                data-test-selector="archiveDocsSearchWithin"
                                id="archiveDocsKeywordSearch"
                            />
                            <GridItem {...styles.subResourceNameLinkItem} id="lanagugaeGrid">
                                {this.state.languageData.length > 1 && (
                                    <DocumentsListFiltersAccordionSection
                                        key="lang"
                                        title={translate("Language")}
                                        facets={this.state.languageData}
                                        onChangeFacet={onChangeDocLang}
                                        showMoreLimit={!isMobile().any ? 3 : 10}
                                        expandByDefault={!isMobile().any}
                                    />
                                )}
                            </GridItem>
                            <GridItem {...styles.subResourceNameLinkItem} id="dataTypeGrid">
                                {this.state.typesData.length > 1 && (
                                    <DocumentsListFiltersAccordionSection
                                        key="type"
                                        title={translate("Type")}
                                        facets={this.state.typesData}
                                        onChangeFacet={onChangeDocType}
                                        showMoreLimit={!isMobile().any ? 3 : 10}
                                        expandByDefault={!isMobile().any}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.ResourceListItemContainer}>
                        <StyledWrapper {...styles.tableContainer} data-test-selector="documentListTable">
                            <DataTable {...styles.dataTable}>
                                <DataTableHead>
                                    <DataTableHeader {...styles.columnHeader}>{translate("MODEL #")}</DataTableHeader>
                                    <DataTableHeader {...styles.columnHeader}>{translate("LANGUAGE")}</DataTableHeader>
                                    <DataTableHeader {...styles.columnHeader}>{translate("TYPE")}</DataTableHeader>
                                </DataTableHead>
                                <DataTableBody data-test-selector="documentListTable_tableBody">
                                    {this.state.docsListData?.map((documentRow: IntDocumentsListModel) => {
                                        return (
                                            <DataTableRow key={Math.random().toString()} {...styles.columnData}>
                                                <DataTableCell {...styles.languageCellCss}>
                                                    {documentRow?.fields?.isPdpLink && documentRow?.fields?.pdpLink ? (
                                                        <Link
                                                            href={documentRow.fields.pdpLink}
                                                            target="_blank"
                                                            {...styles.modelLinks}
                                                        >
                                                            {documentRow.fields.name}
                                                        </Link>
                                                    ) : (
                                                        <StyledWrapper {...styles.modelLinksText}>
                                                            {documentRow.fields.name}
                                                        </StyledWrapper>
                                                    )}
                                                </DataTableCell>
                                                <DataTableCell {...styles.cellCss}>
                                                    {documentRow?.fields?.language
                                                        ?.split(",")
                                                        .map(lang => translate(lang))
                                                        .join(", ")}
                                                </DataTableCell>
                                                <DataTableCell {...styles.cellCss}>
                                                    <Link
                                                        href={documentRow.fields.source}
                                                        download=""
                                                        {...styles.typeLinks}
                                                        target="_blank"
                                                    >
                                                        {translate(documentRow.fields.type || "Instructions")}
                                                    </Link>
                                                </DataTableCell>
                                            </DataTableRow>
                                        );
                                    })}
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </>
        );
    }
}
const documentTab = {
    displayName: "Document",
    sortOrder: 0,
};

const languageTab = {
    displayName: "Languages",
    sortOrder: 1,
};

const typesTab = {
    displayName: "Type",
    sortOrder: 2,
};

const doumentsList: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(IntDocumentsList)),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.documents,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "Translatable",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: fields.name,
                        displayName: "Document Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: documentTab,
                    },
                    {
                        name: fields.isPdpLink,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tab: documentTab,
                    },
                    {
                        name: fields.pdpLink,
                        displayName: "Enter PDP link",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: documentTab,
                        isVisible: item => item?.fields.isPdpLink === true,
                    },
                    {
                        name: fields.source,
                        displayName: "Document",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tab: documentTab,
                    },
                    {
                        name: fields.isEnglish,
                        displayName: "English",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tab: languageTab,
                    },
                    {
                        name: fields.isFrench,
                        displayName: "French",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tab: languageTab,
                    },
                    {
                        name: fields.isSpanish,
                        displayName: "Spanish",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tab: languageTab,
                    },
                    {
                        name: fields.type,
                        displayName: "Type",
                        editorTemplate: "RadioButtonsField",
                        options: [
                            {
                                displayName: "Instructions",
                                value: "Instructions",
                            },
                        ],
                        defaultValue: "Instructions",
                        tab: typesTab,
                    },
                ],
            },
        ],
    },
};

export default doumentsList;
